import { activeSiteConfig } from "@config/site/site-config";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { IconButton } from "@ecco/ui/icon-button";
import { Box } from "@ecco/ui/box";
import { Image } from "@ecco/ui/image";
import { space } from "@ecco/ui/tokens";
import { isNumber } from "lodash-es";
import { useCallback, use, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { StoresToggle } from "./components/stores-toggle/StoresToggle";
import type { StoreData } from "@features/stores/types";
import { Controller } from "./controller";
import { icon } from "./icons";
import { createMarker, storeMarkerIcon } from "./map-markers";
import { clusterAlgorithm, clusterRenderer } from "./map-options";
import { calculateZoomLevel } from "./utils/calculateZoomLevel";
import { isEccoStore } from "./utils/isEccoStore";
import { getMarkersOnMap } from "./utils/getMarkersOnMap";
import { MAP_CONTAINER } from "@e2e/helpers/store-locator/constants/common";
import { findNearestPoint } from "./utils/findNearestGeoPoint";
import { ShippingMethodTypes } from "@features/checkout/types";
import { googleMapsUtils } from "@lib/maps/googleMapsUtils";
import { useLocale } from "next-intl";
import { parseSearchResultLocation } from "./utils/parseAddressComponents";
const minZoom = activeSiteConfig.geo.initZoomLevel ?? 5;
const MAX_MAP_ZOOM = 15;
const ZOOM_ON_SELECT = 15;

type Markers = {
    [storeId: string]: google.maps.Marker;
};

enum MapState {
    Unmounted,
    Mounted,
    Ready,
    Completed,
}

const MAP_CLUSTER_ZOOM = 12;

interface StoreLocatorProps {
    hasStock?: boolean;
    isExtraLargeViewport?: boolean;
    shippingMethodKey?: ShippingMethodTypes;
    searchResults?: StoreData[];
    /** Stores to frame once this map has settled - one is zoomed to, several are fitted. Checkout
     *  v2's compact dialog remounts the map per visit, and a fresh map resets to the whole market. */
    frameStoreIds?: string[];
    /** Replaces the default and selected pins. Left unset, the map keeps its own pins. */
    pinIcons?: Record<"default" | "selected", MapPinIcon>;
    /** Where a drag left the map centred, for a caller that searches the area dragged to. */
    onDragEnd?: (center: { lat: number; lng: number }) => void;
    /** Fit to the nearest store only for the map's own search results, so pins a caller adds
     *  for a dragged-to area do not pull the map back. Left unset, every redraw fits as before. */
    fitsOnlyToSearchResults?: boolean;
}

export type MapPinIcon = { url: string; width: number; height: number };

let map: google.maps.Map;

export const StoreLocatorMap = ({
    hasStock,
    isExtraLargeViewport,
    shippingMethodKey,
    searchResults,
    frameStoreIds,
    pinIcons,
    onDragEnd,
    fitsOnlyToSearchResults = false,
}: StoreLocatorProps) => {
    const locale = useLocale();
    // The listener is attached once, when the map is built; the ref keeps the latest callback.
    const onDragEndRef = useRef(onDragEnd);
    useEffect(() => {
        onDragEndRef.current = onDragEnd;
    }, [onDragEnd]);

    const [idle, setIdle] = useState<number>(0);
    const [markers, setMarkers] = useState<Markers>({});
    const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer | null>(null);
    const [clientMarker, setClientMarker] = useState<google.maps.Marker>();
    const [, setSelectedMarker] = useState<string>("");
    const pendingFitRef = useRef<(() => void) | null>(null);

    const {
        selectedStoreId,
        setSelectedStoreId,
        setHighlightSelectedStore,
        storeData,
        clientLocation,
        setVisibleStores,
        setLastSelect,
        searchResult,
        setMapBounds,
        mapBounds,
        storesWithAvailability,
        setStoreData,
        setIsClickAndCollect,
        showPartnerStores,
        getShouldShowStore,
        mapState,
        storeDataIndex,
        setMapState,
        isClickAndCollect,
        setIsLoading,
        filterDistance,
        setFilterDistance,
        setClientLocation,
        storeLocatorLocation,
        searchValidation,
    } = use(Controller);

    const fitMapToNearestStore = (
        location: google.maps.LatLng,
        markers: Markers,
        adjustZoom = true
    ) => {
        const { nearestPoint } = findNearestPoint(location, Object.values(markers));
        const nearestLocation = nearestPoint ?? location;

        const bounds = new google.maps.LatLngBounds();
        bounds.extend(location);
        bounds.extend(nearestLocation);
        map.fitBounds(bounds, 100);

        if (adjustZoom) {
            google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                adjustMapZoom(map);
            });
        }
    };

    useEffect(() => {
        // Clear any previously found pick-up points when the searched postal code is restricted
        if (searchValidation.isRestricted) {
            setStoreData([]);
        }
    }, [searchValidation.isRestricted]);

    useEffect(() => {
        if (searchResults) {
            setStoreData(searchResults);
            setFilterDistance(null);
            if (shippingMethodKey !== ShippingMethodTypes.PARCEL_SHOP) {
                setClientMarker(null);
            }
            setMapState(MapState.Ready);
            setIsLoading(false);
        }
    }, [searchResults]);

    useEffect(() => {
        if (shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP) {
            setIsClickAndCollect(true);
            return;
        }
        if (!storesWithAvailability) return;

        setIsClickAndCollect(true);
        setIsLoading(true);
        // Force map state back to Ready to trigger marker recreation
        if (markerClusterer) {
            setMapState(MapState.Ready);
        }
    }, [
        storesWithAvailability,
        setIsClickAndCollect,
        setIsLoading,
        setMapState,
        shippingMethodKey,
        markerClusterer,
    ]);

    const adjustMapZoom = (map: google.maps.Map) => {
        const currentZoom = map.getZoom();
        if (currentZoom > MAX_MAP_ZOOM) map.setZoom(MAX_MAP_ZOOM);
        else if (currentZoom < minZoom) map.setZoom(minZoom);
    };

    const icons = {
        default: icon.storePin.src,
        selected: icon.storePinSelected.src,
        partner: icon.partnerPin.src,
    };
    if (shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP) {
        icons.default = icon.parcelShopPin.src;
        icons.selected = icon.parcelShopPinSelected.src;
        icons.partner = icon.parcelShopPin.src;
    }
    // A caller's own pin takes the partner's place too, so every unselected pin looks alike.
    const markerIcon = (kind: keyof typeof icons): google.maps.Icon => {
        const pin = pinIcons?.[kind === "partner" ? "default" : kind];
        return pin
            ? { url: pin.url, scaledSize: new google.maps.Size(pin.width, pin.height) }
            : storeMarkerIcon(icons[kind]);
    };

    useEffect(() => {
        if (markerClusterer && mapState === MapState.Ready) {
            const handleSelectedStore = (storeId: string) => {
                setSelectedStoreId(storeId);
                setLastSelect(Date.now());
            };

            const markerCollection = (storesWithAvailability ?? storeData).reduce<Markers>(
                (collection, store) => {
                    // eslint-disable-next-line no-param-reassign
                    collection[store?.storeId] = createMarker({
                        icon: markerIcon(isEccoStore(store) ? "default" : "partner"),
                        position: new google.maps.LatLng(store?.latitude, store?.longitude),
                        title: store?.name,
                        zIndex: 1,
                        onClick: () => {
                            map.setValues({
                                ...(map.getZoom() < ZOOM_ON_SELECT && { zoom: ZOOM_ON_SELECT - 1 }),
                                center: new google.maps.LatLng(store?.latitude, store?.longitude),
                            });
                            handleSelectedStore(store?.storeId);
                        },
                        visible: getShouldShowStore(store?.storeId),
                    });
                    return collection;
                },
                {}
            );

            const markersOnMap = getMarkersOnMap(markerCollection, map);

            markerClusterer.clearMarkers();
            markerClusterer.addMarkers(Object.values(markersOnMap));

            setMarkers(markerCollection ?? {});
            setMapState(MapState.Completed);
            if (isClickAndCollect) {
                setIsLoading(false);
            }
            // Adjust zoom to ensure at least one service point is visible
            const isOwnSearchDraw = !fitsOnlyToSearchResults || storeData === searchResults;
            if (clientLocation && !!searchResult && isOwnSearchDraw) {
                const isCountryResult = searchResult?.object?.types?.find(
                    (type) => type === "country"
                );
                if (!isCountryResult) {
                    const searchLocation = new google.maps.LatLng(
                        clientLocation.lat,
                        clientLocation.lng
                    );
                    // Cached stores can plot before the first idle, whose reset would undo the fit.
                    if (idle === 0) {
                        pendingFitRef.current = () =>
                            fitMapToNearestStore(searchLocation, markerCollection);
                    } else {
                        fitMapToNearestStore(searchLocation, markerCollection);
                    }
                }
            }
        } else if (mapState === MapState.Completed) {
            setSelectedMarker((prevSelectedStoreId) => {
                if (prevSelectedStoreId !== selectedStoreId) {
                    markers[prevSelectedStoreId]?.setIcon(
                        markerIcon(
                            isEccoStore(storeDataIndex[prevSelectedStoreId]) ? "default" : "partner"
                        )
                    );
                    markers[prevSelectedStoreId]?.setZIndex(1);
                }

                if (selectedStoreId !== "") {
                    markers[selectedStoreId]?.setIcon(markerIcon("selected"));
                    markers[selectedStoreId]?.setZIndex(2);
                }

                return selectedStoreId;
            });

            if (selectedStoreId !== "" && mapState === MapState.Completed) {
                setHighlightSelectedStore((prevState) => {
                    if (prevState) {
                        if (map && map?.getZoom() < ZOOM_ON_SELECT) {
                            map?.panTo(
                                markers?.[selectedStoreId]?.getPosition() ||
                                    clientMarker?.getPosition() ||
                                    mapBounds?.getCenter()
                            );
                            map?.setZoom(ZOOM_ON_SELECT);
                        }
                        return true;
                    }
                    return false;
                });
            }
        }
    }, [
        mapState,
        storeData,
        markers,
        selectedStoreId,
        setSelectedStoreId,
        setLastSelect,
        setHighlightSelectedStore,
        storesWithAvailability,
        markerClusterer,
        clientLocation,
    ]);

    useEffect(() => {
        if (mapState === MapState.Mounted && (storeData.length > 0 || hasStock)) {
            setMapState(MapState.Ready);
        }
    }, [mapState, storeData, hasStock]);

    useEffect(() => {
        if (map && mapState === MapState.Completed) {
            const center = map?.getCenter();
            const bounds = map?.getBounds();

            const ne = bounds?.getNorthEast();
            const sw = bounds?.getSouthWest();

            const lat = center?.lat();
            const lng = center?.lng();

            const latLow = lat - (lat - sw?.lat());
            const latHigh = lat + (ne?.lat() - lat);
            const lngLow = lng - (lng - sw?.lng());
            const lngHigh = lng + (ne?.lng() - lng);
            const rectangleBounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(latLow, lngLow),
                new google.maps.LatLng(latHigh, lngHigh)
            );

            const visibleStore = Object.entries(markers).reduce((storeIndex, [storeId, marker]) => {
                if (rectangleBounds.contains(marker.getPosition())) {
                    storeIndex.push(storeId);
                }

                return storeIndex;
            }, [] as string[]);

            setVisibleStores(visibleStore);
        }
    }, [mapState, idle, markers]);

    useEffect(() => {
        if (!markers || !markerClusterer || !map) return;

        if (map.getZoom() < MAP_CLUSTER_ZOOM) {
            markerClusterer.clearMarkers();
        }

        const markersOnMap = getMarkersOnMap(markers, map);

        Object.entries(markers).forEach(([index, marker]) => {
            marker.setVisible(getShouldShowStore(index));
        });

        markerClusterer.addMarkers(Object.values(markersOnMap));
        if (clientMarker) {
            markerClusterer.addMarker(clientMarker);
            clientMarker.setVisible(true);
        }
    }, [showPartnerStores, markers, markerClusterer, map, searchResult, mapBounds, clientLocation]);

    useEffect(() => {
        if (!clientLocation || !markerClusterer || !map) return;
        const position = new google.maps.LatLng(clientLocation?.lat, clientLocation?.lng);
        const isParcelShop = shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;

        // Create the client marker if it doesn't exist. Otherwise update it's position.
        if (!clientMarker) {
            const marker = createMarker({
                icon: storeMarkerIcon(icon.clientPin.src),
                position,
                zIndex: 3,
            });

            markerClusterer.addMarker(marker);
            setClientMarker(marker);
        } else {
            clientMarker.setPosition(clientLocation);
            clientMarker.setVisible(true);
        }

        // For PS we dont fit to stores as they are async loaded. We just set the center at get stores from there.
        if (isParcelShop) {
            map.setCenter(position);
            return;
        }

        // For non PS we should have all stores available and we can fit to the nearest one.
        if (markers) {
            fitMapToNearestStore(
                new google.maps.LatLng(clientLocation.lat, clientLocation.lng),
                markers
            );
        }
    }, [clientMarker, clientLocation]);

    useEffect(() => {
        if (map && !!searchResult) {
            const location = searchResult?.coordinates;
            if (!location) return;

            if (shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP) {
                setClientLocation(location.toJSON());
                return;
            }

            const { viewport } = parseSearchResultLocation(searchResult);

            const isCountryResult = searchResult?.object?.types?.find((type) => type === "country");
            const initialZoom = activeSiteConfig.geo.initZoomLevel ?? 5;
            if (isCountryResult) {
                const { nearestPoint } = findNearestPoint(location, Object.values(markers));
                const newCenter = nearestPoint ?? location;
                if (viewport) {
                    map.fitBounds(viewport);
                }
                map.setCenter(newCenter);
                map.setZoom(isExtraLargeViewport ? initialZoom : initialZoom - 2);
                return;
            }

            // If we have stores and user searched for a location
            if (Object.keys(markers).length > 0) {
                fitMapToNearestStore(location, markers);
                return;
            }

            // Fallback if we have a viewport in the result
            if (viewport) {
                map.fitBounds(viewport);
            }
        }
    }, [searchResult]);

    useEffect(() => {
        if (filterDistance && mapState === MapState.Completed) {
            let center = map.getCenter();
            let zoom;
            if (isNumber(filterDistance.value)) {
                zoom = calculateZoomLevel(filterDistance.value);
            } else {
                zoom =
                    (isExtraLargeViewport
                        ? activeSiteConfig.geo.initZoomLevel
                        : activeSiteConfig.geo.initZoomLevel - 2) ?? 10;
                center = new google.maps.LatLng(
                    activeSiteConfig?.geo?.centerLat,
                    activeSiteConfig?.geo?.centerLng
                );
            }
            map.setCenter(center);
            map.setZoom(zoom);
        }
    }, [filterDistance?.value, filterDistance?.label]);

    useEffect(() => {
        setFilterDistance(null);
    }, [map?.getBounds, map?.getCenter()]);

    const mapRef = useCallback(
        (node: HTMLDivElement) => {
            try {
                if (node === null) {
                    setIdle(0);
                    setMapState(MapState.Unmounted);
                    return;
                }

                const { geo } = activeSiteConfig;

                googleMapsUtils
                    .loadMaps(locale)
                    .then(() => {
                        map = new google.maps.Map(node, {
                            disableDefaultUI: true,
                            clickableIcons: false,
                            maxZoom: 16,
                            mapId: process.env.NEXT_PUBLIC_GOOGLE_MAP_ID,
                        });
                        Logger.info(ServiceType.WEB, "Google Maps render", {
                            tag: LogTag.STORES,
                            location: storeLocatorLocation,
                        });

                        map.fitBounds(
                            new google.maps.LatLngBounds(
                                new google.maps.LatLng(geo?.swLat, geo?.swLng),
                                new google.maps.LatLng(geo?.neLat, geo?.neLng)
                            )
                        );

                        const mapControlNode = document.createElement("div");

                        const handleZoom = (level: number) => {
                            setFilterDistance(null);
                            map.setZoom(map.getZoom() + level);
                        };

                        const root = createRoot(mapControlNode);
                        root.render(
                            <Box
                                data-testid="store-locator-map"
                                mr="12px"
                                mb={2}
                                display="flex"
                                flexDirection="row"
                                gap={2}
                            >
                                <IconButton
                                    w={space["6.8"]}
                                    h={space["6.8"]}
                                    aria-label="Zoom Out"
                                    icon={
                                        <Image
                                            w="100%"
                                            h="100%"
                                            src={icon.zoomOut.src}
                                            alt="Zoom Out"
                                        />
                                    }
                                    onClick={() => handleZoom(-1)}
                                />
                                <IconButton
                                    w={space["6.8"]}
                                    h={space["6.8"]}
                                    aria-label="Zoom In"
                                    icon={
                                        <Image
                                            w="100%"
                                            h="100%"
                                            src={icon.zoomIn.src}
                                            alt="Zoom In"
                                        />
                                    }
                                    onClick={() => handleZoom(1)}
                                />
                            </Box>
                        );

                        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(mapControlNode);

                        setMarkerClusterer(
                            new MarkerClusterer({
                                map,
                                renderer: clusterRenderer,
                                algorithm: clusterAlgorithm,
                            })
                        );

                        map.addListener("idle", () => {
                            setIdle((prevIdle) => {
                                if (prevIdle === 0) {
                                    const initialZoom = activeSiteConfig.geo.initZoomLevel ?? 5;
                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                    isExtraLargeViewport
                                        ? map.setZoom(initialZoom)
                                        : map.setZoom(initialZoom - 2);
                                    map.setCenter(
                                        new google.maps.LatLng(geo?.centerLat, geo?.centerLng)
                                    );
                                }
                                return Date.now();
                            });
                            setIsLoading(false);
                            setMapBounds(map.getBounds());
                        });
                        map.addListener("dragend", () => {
                            setFilterDistance(null);
                            const center = map.getCenter();
                            if (center) onDragEndRef.current?.(center.toJSON());
                        });
                        map.addListener("click", () => {
                            setFilterDistance(null);
                        });
                    })
                    .catch((error) => {
                        Logger.error(ServiceType.WEB, "Google Maps load error", {
                            tag: LogTag.ERROR,
                            error,
                        });
                        setMapState(MapState.Unmounted);
                    });
            } catch (error) {
                Logger.error(ServiceType.WEB, "StoreLocatorMap", {
                    tag: LogTag.STORES,
                    error,
                });
            }
        },
        [locale, setMapBounds]
    );

    useEffect(() => {
        if (idle !== 0) return;
        setMapState(MapState.Mounted);
    }, [idle]);

    // idle leaves 0 only once the first-idle reset has run, so a fit held back for it lands now.
    useEffect(() => {
        if (idle === 0 || !pendingFitRef.current) return;
        const fit = pendingFitRef.current;
        pendingFitRef.current = null;
        fit();
    }, [idle]);

    // Once per map: the first idle has run its reset by the time idle leaves 0, and a later
    // pick on this same map is the shopper's own pin click, which frames itself.
    const hasFramedStoresRef = useRef(false);
    useEffect(() => {
        if (!frameStoreIds?.length || idle === 0 || mapState !== MapState.Completed) return;
        if (hasFramedStoresRef.current) return;
        const positions = frameStoreIds
            .map((storeId) => markers[storeId]?.getPosition())
            .filter((position): position is google.maps.LatLng => Boolean(position));
        if (positions.length === 0) return;

        hasFramedStoresRef.current = true;
        if (positions.length === 1) {
            map.setCenter(positions[0]);
            map.setZoom(ZOOM_ON_SELECT);
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        positions.forEach((position) => bounds.extend(position));
        map.fitBounds(bounds, 40);
        google.maps.event.addListenerOnce(map, "bounds_changed", () => adjustMapZoom(map));
    }, [frameStoreIds, idle, mapState, markers]);

    return (
        <>
            {!isClickAndCollect && <StoresToggle />}
            <Box
                data-testid={MAP_CONTAINER}
                data-markers-on-map-count={
                    getMarkersOnMap(markers, map).filter((marker) => marker.getVisible()).length
                }
                data-map-ready={mapState === MapState.Completed}
                position="absolute"
                width="100%"
                height="100%"
                ref={mapRef}
            />
        </>
    );
};
